import { Divider, Link, Stack } from '@mui/material';
import { useState } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';

import { type api } from 'common';

import { EntitySearch } from './EntitySearch';
import { FlowParameters } from './FlowParameters';
import { MockInputs } from './MockInputs';
import { PracticeAutocomplete } from './PracticeAutocomplete';
import { TestRunEntitySourceVariant } from './config';
import { useFlowDefinitionContext } from '../../../../context';
import { useReactFlowViewContext } from '../../../context/context';
import { BlockRadioGroupField } from '../../ConfigFields/RadioGroupField';
import { BlockSelectField } from '../../ConfigFields/SelectField';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

export function StartFormFields() {
  const { flowDefinition } = useFlowDefinitionContext();
  const { isEditable } = useReactFlowViewContext();
  const { value: entityType } = useSelectedNodeField<string>('config.entityType');
  const { value: entityId, setValue: setEntityId } = useSelectedNodeField<string | undefined>(
    'config.testEntityId'
  );
  const { setValue: setPracticeId } = useSelectedNodeField<string | undefined>(
    'config.testPracticeId'
  );
  const [entity, setEntity] = useState<api.Search.Result | undefined>(undefined);
  const { value: testRunEntitySourceVariant } = useSelectedNodeField<string>(
    'config.testRunEntitySourceVariant'
  );

  return (
    <Stack spacing={2}>
      <BlockSelectField
        fieldName="config.entityType"
        label="Entity Type"
        required
        onChange={async () => {
          setEntityId(undefined);
          setPracticeId(undefined);
          setEntity(undefined);
        }}
        options={[
          { label: 'Patient', value: 'Patient' },
          { label: 'Encounter', value: 'Encounter' },
          { label: 'Order', value: 'Order' },
          { label: 'External Document Packet', value: 'ExternalDocumentPacket' },
          { label: 'None', value: 'None' },
        ]}
      />
      {entityType === 'Patient' && (
        <BlockRadioGroupField
          label="Use production patient?"
          row
          defaultValue={TestRunEntitySourceVariant.Test}
          options={[
            {
              label: 'Yes (Real patient from EHR)',
              value: TestRunEntitySourceVariant.Production,
            },
            {
              label: (
                <>
                  No (Test patient from{' '}
                  <Link component={ReactRouterLink} to="/flow-tester" target="_blank">
                    Flow Tester
                  </Link>
                  )
                </>
              ),
              value: TestRunEntitySourceVariant.Test,
            },
          ]}
          fieldName="config.testRunEntitySourceVariant"
        />
      )}
      {entityType && entityType !== 'None' && (
        <EntitySearch
          disabled={!isEditable}
          organizationId={flowDefinition.organizationId}
          entityType={entityType}
          entityId={entityId}
          entity={entity}
          setEntity={setEntity}
          testRunEntitySourceVariant={
            // For now, only allow production entity search for patients
            entityType === 'Patient'
              ? (testRunEntitySourceVariant as TestRunEntitySourceVariant | undefined)
              : TestRunEntitySourceVariant.Test
          }
        />
      )}
      {(entityType === 'None' || entityType === 'ExternalDocumentPacket') && (
        <PracticeAutocomplete fieldName="config.testPracticeId" label="Test Practice" />
      )}
      <Divider />
      <FlowParameters fieldName="config.parameters" entityType={entityType} />
      <Divider />
      <MockInputs />
    </Stack>
  );
}
